'use client';
import { useDebounce } from 'ahooks';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { Button } from '@/app/components/ui/button';
import { HOME_PAGE } from '@/app/constants';
import { reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import type { FeatureCarouselItem } from '../../_components/FeatureCarousel';
import ProductInputTool from '../../_components/ProductInputTool';
import { reportProductPageElementClick } from '../../_components/ProductInputTool/utils';

const FeatureCarousel = dynamic(() => import('../../_components/FeatureCarousel').then((mod) => mod.FeatureCarousel));
const ProductSteps = dynamic(() => import('@/app/components/shared/ProductSteps').then((mod) => mod.ProductSteps));
const ComparisonSection = dynamic(() => import('./ComparisonSection').then((mod) => mod.ComparisonSection));
const QuestionSection = dynamic(() => import('./QuestionSection').then((mod) => mod.QuestionSection));
const RelatedProductsSection = dynamic(() =>
  import('../../_components/RelatedProductsSection').then((mod) => mod.RelatedProductsSection),
);

export const ReferenceToVideo: React.FC = () => {
  const router = useRouter();
  const t = useTranslations('ai-reference-to-video');

  useEffect(() => {
    reportEvent('feature_page_view', {
      from_page: 'reference to video',
    });
  }, []);

  // 使用 react-intersection-observer
  const intersectionConfig = {
    threshold: 0.5,
    rootMargin: '0px 0px -20% 0px', // 当元素进入视口80%时触发
  };

  const { ref: line1Ref, inView: line1InView } = useInView(intersectionConfig);
  const { ref: line2Ref, inView: line2InView } = useInView(intersectionConfig);
  const { ref: line3Ref, inView: line3InView } = useInView(intersectionConfig);
  const { ref: videoRef, inView: videoInView } = useInView(intersectionConfig);

  // 处理生成按钮点击
  const handleGenerateClick = () => {
    reportEvent('element_click', {
      element_name: 'generate-now',
      page: 'ai-reference-to-video',
    });
    reportProductPageElementClick('generate now');
    router.push(HOME_PAGE);
  };

  // 特性数据
  const features = [
    {
      id: 'feature-1',
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/bar-d379334e.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/bar-d379334e-7a1b6098.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/bar-8d352a21.webp',
      mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/bar-8d352a21-c08b0fff.webp',
    },
  ];

  // 优势数据
  const advantages = [
    {
      id: 'advantage-1',
      title: t('advantage.sameSubject.title'),
      description: t('advantage.sameSubject.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/knight-22d96b7a.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/knight-22d96b7a-ad4238b0.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/knight-326f0ac8.webp',
      mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/knight-326f0ac8-707301e1.webp',
    },
    {
      id: 'advantage-2',
      title: t('advantage.flexibleReferenceCreation.title'),
      description: t('advantage.flexibleReferenceCreation.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/advantage-2-529d3394.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/advantage-2-529d3394-52342785.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/advantage-2-9b8233d5.webp',
      mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/advantage-2-9b8233d5-222f4995.webp',
    },
    {
      id: 'advantage-3',
      title: t('advantage.sameScene.title'),
      description: t('advantage.sameScene.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/hotdog-a3f23684.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/hotdog-a3f23684-ab4d004c.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/hotdog-3624e6b4.webp',
      mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/hotdog-3624e6b4-9d64a40f.webp',
    },
    {
      id: 'advantage-4',
      title: t('advantage.moreCharacters.title'),
      description: t('advantage.moreCharacters.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/burger-7536df6c.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/burger-7536df6c-35bf78ae.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/burger-3d597dd2.webp',
      mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/burger-3d597dd2-d7c0f83d.webp',
    },
  ];

  // 使用场景数据
  const useCases = [
    {
      id: 'usecase-1',
      title: t('useCase.personalIP.title'),
      description: t('useCase.personalIP.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/magic-3b8e0fdb.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/magic-3b8e0fdb-20b6f53d.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/magic-5e1ded8d.webp',
      mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/magic-5e1ded8d-ba3a526c.webp',
    },
    {
      id: 'usecase-2',
      title: t('useCase.narrativeShorts.title'),
      description: t('useCase.narrativeShorts.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/cowboy-04ae59ff.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/cowboy-04ae59ff-8b84ed56.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/cowboy-205e8068.webp',
      mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/cowboy-205e8068-bca6e4c7.webp',
    },
    {
      id: 'usecase-3',
      title: t('useCase.advertising.title'),
      description: t('useCase.advertising.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/wedding-a01a2526.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/wedding-a01a2526-7de0a055.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/wedding-0dc5c152.webp',
      mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/wedding-0dc5c152-3ea4881b.webp',
    },
    {
      id: 'usecase-4',
      title: t('useCase.fun.title'),
      description: t('useCase.fun.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/maintain_visual-b8d01b6a.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/maintain_visual-b8d01b6a-6ee0ed52.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/maintain_visual-c17fe8b6.webp',
      mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/maintain_visual-c17fe8b6-5772b973.webp',
    },
  ];

  // 保持一致性的常见问题数据（共用同一只小猫视频）
  const keepConsistentVideo = {
    videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/cat-42d94186.mp4',
    mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/cat-42d94186-b0d249a1.mp4',
    videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/cat-9fef0a57.webp',
    mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/cat-9fef0a57-fa52bb9b.webp',
  };
  const keepConsistentIssues: FeatureCarouselItem[] = [
    {
      id: 'keep-consistent-faces-outfits',
      title: t('keepConsistent.issues.facesOutfits.title'),
      description: t('keepConsistent.issues.facesOutfits.description'),
      ...keepConsistentVideo,
    },
    {
      id: 'keep-consistent-objects',
      title: t('keepConsistent.issues.objects.title'),
      description: t('keepConsistent.issues.objects.description'),
      ...keepConsistentVideo,
    },
    {
      id: 'keep-consistent-continuity',
      title: t('keepConsistent.issues.continuity.title'),
      description: t('keepConsistent.issues.continuity.description'),
      ...keepConsistentVideo,
    },
  ];

  // 步骤数据
  const stepItems = [
    {
      index: 1,
      title: t('step.step1.title'),
      description: t('step.step1.description'),
      image: 'https://image01.cf.vidu.studio/vidu/media-asset/step1-8ce91ed8.webp',
      alt: 'How to use Vidu Reference to Video — upload references',
    },
    {
      index: 2,
      title: t('step.step2.title'),
      description: t('step.step2.description'),
      image: 'https://image01.cf.vidu.studio/vidu/media-asset/step2-ea30db81.webp',
      alt: 'How to use Vidu Reference to Video — write prompt',
    },
    {
      index: 3,
      title: t('step.step3.title'),
      description: t('step.step3.description'),
      image: 'https://image01.cf.vidu.studio/vidu/media-asset/step3-c06d14a4.webp',
      alt: 'How to use Vidu Reference to Video — generate video',
    },
  ];
  // 使用 ahooks 的 useDebounce 来避免频繁的状态变化
  const shouldAnimate = useDebounce(line1InView && videoInView, { wait: 100 });

  return (
    <div className="flex min-h-screen w-full flex-col bg-black text-white">
      {/* Hero Section */}
      <section className="pt-[164px] sm:pt-16">
        <div className="mx-auto flex flex-col items-center px-4">
          <div className="w-full max-w-[57vw] text-center">
            <h1 className="mb-4 font-semibold text-5xl leading-tight sm:text-4xl">{t('title')}</h1>
            <p className="mb-12 font-normal text-xl sm:mb-6 sm:text-base">{t('subtitle')}</p>
          </div>
          <div className="mx-auto min-h-[227px] w-full max-w-[57vw] md:max-w-full">
            <ProductInputTool />
          </div>
          <div className="mt-12 w-[74vw] overflow-hidden rounded-xl sm:w-full">
            <VideoLazyLoad
              videoSrc="https://image01.cf.vidu.studio/vidu/media-asset/banner-2b999631-99237e3e.mp4"
              mobileVideoSrc="https://image01.cf.vidu.studio/vidu/media-asset/banner-2b999631-91e35943.mp4"
              poster="https://image01.cf.vidu.studio/vidu/media-asset/banner-ac011111.webp"
              priority={true}
              alt="Vidu reference to video example — consistent characters across scenes"
            />
          </div>
        </div>
      </section>

      {/* Create Without Limits Section */}
      <section className="pt-[120px] sm:pt-10">
        <div className="mx-auto flex flex-col items-center px-4">
          <div className="w-[74vw] text-center sm:w-full">
            <h2 className="mb-16 font-semibold text-[32px] leading-tight sm:text-2xl md:text-[28px]">
              {t('createWithoutLimits.title')}
            </h2>
            <div className="mb-8 font-medium text-[40px] text-system-white80 leading-[140%] sm:text-2xl md:text-4xl">
              <div
                ref={line1Ref}
                className={`mb-6 flex flex-wrap items-center justify-center gap-2 leading-tight transition-all duration-1000 ${
                  line1InView ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
              >
                <span className="inline-flex items-center rounded-full bg-system-white12 px-6 py-2 font-Unna font-normal text-[54px] text-white sm:hidden">
                  <div className="mr-2 h-[54px] w-[54px] overflow-hidden rounded-full">
                    <img
                      src="https://image01.cf.vidu.studio/vidu/media-asset/cowboy-191aeb6a.webp"
                      alt="Vidu reference to video example — cowboy character consistency"
                      className="h-full w-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  {t('createWithoutLimits.description.caption1')}
                </span>
                <span className="hidden sm:block">
                  <span className="font-medium">{t('createWithoutLimits.description.caption1')}</span>
                </span>
                <span className="font-medium">{t('createWithoutLimits.description.line1')}</span>
                <span className="inline-flex items-center rounded-full bg-system-white12 px-6 py-2 font-Unna font-normal text-[54px] text-white sm:hidden">
                  <div className="mr-2 h-[54px] w-[54px] overflow-hidden rounded-full">
                    <img
                      src="https://image01.cf.vidu.studio/vidu/media-asset/bar-61038e68.webp"
                      alt="Vidu reference to video example — bar scene consistency"
                      className="h-full w-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  {t('createWithoutLimits.description.caption2')}
                </span>
                <span className="hidden sm:block">
                  <span className="font-medium">{t('createWithoutLimits.description.caption2')}</span>
                </span>
              </div>
              <div
                ref={line2Ref}
                className={`mb-6 flex flex-wrap items-center justify-center gap-2 leading-tight transition-all delay-300 duration-1000 ${
                  line2InView ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
              >
                <span className="font-medium">{t('createWithoutLimits.description.line2word1')}</span>
                <span className="inline-flex items-center rounded-full bg-system-white12 px-6 py-2 font-Unna font-normal text-[54px] text-white sm:hidden">
                  <div className="mr-2 h-[54px] w-[54px] overflow-hidden rounded-full">
                    <img
                      src="https://image01.cf.vidu.studio/vidu/media-asset/lady-3547eb59.webp"
                      alt="Vidu reference to video example — lady character consistency"
                      className="h-full w-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  {t('createWithoutLimits.description.caption3')}
                </span>
                <span className="hidden sm:block">
                  <span className="font-medium">{t('createWithoutLimits.description.caption3')}</span>
                </span>
                <span className="font-medium">{t('createWithoutLimits.description.line2word2')}</span>
              </div>
              <div
                ref={line3Ref}
                className={`mb-6 flex flex-wrap items-center justify-center gap-2 leading-tight transition-all delay-500 duration-1000 ${
                  line3InView ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
              >
                <span className="font-medium">{t('createWithoutLimits.description.line3')}</span>
              </div>
            </div>
          </div>
          <div
            ref={videoRef}
            className={`mt-16 w-[74vw] overflow-hidden rounded-xl transition-all duration-1000 sm:w-full ${
              shouldAnimate ? 'translate-y-[-240px] opacity-70 sm:translate-y-[-120px]' : 'translate-y-0 opacity-100'
            }`}
          >
            <VideoLazyLoad
              videoSrc={features[0].videoSrc}
              poster={features[0].videoPoster}
              mobilePoster={features[0].mobilePoster}
              alt="Vidu reference to video example — character consistency demonstration"
            />
          </div>
        </div>
      </section>

      <section className="pt-[120px] sm:pt-10">
        <div className="mx-auto flex flex-col items-center px-[192px] sm:px-4 md:px-16">
          {/* Advantages Grid */}
          <div className="grid w-[74vw] grid-cols-1 gap-16 md:w-full">
            {advantages.map((advantage, index) => (
              <div
                key={advantage.id}
                className="flex flex-row items-center gap-12 sm:flex-col sm:gap-16 sm:text-center"
              >
                <div className={`flex-1 ${index % 2 === 1 ? 'order-2 sm:order-none' : ''}`}>
                  <h3 className="mb-4 font-semibold text-[40px] md:text-[32px]">{advantage.title}</h3>
                  <p className="mb-6 font-normal text-system-white64 md:text-base">{advantage.description}</p>
                  <Button
                    className="rounded-[100px] border-2 border-[#0086FF] bg-transparent px-8 py-4 font-semibold text-[#0086FF] text-sm shadow-lg transition-all hover:shadow-xl sm:py-4"
                    onClick={handleGenerateClick}
                  >
                    {t('buttons.generateNow')}
                  </Button>
                </div>
                <div className={`flex-1 ${index % 2 === 1 ? 'order-1 sm:order-none' : ''}`}>
                  <div className="overflow-hidden rounded-xl">
                    <VideoLazyLoad
                      videoSrc={advantage.videoSrc}
                      poster={advantage.videoPoster}
                      mobilePoster={advantage.mobilePoster}
                      alt={`Vidu reference to video example — ${advantage.title}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pt-[120px] sm:pt-10">
        <div className="mx-auto flex flex-col items-center px-4">
          <div className="w-[58vw] text-center">
            <h2 className="mb-16 font-semibold text-[40px] leading-tight sm:text-[36px]">{t('useCase.title')}</h2>
          </div>

          <div className="grid w-[74vw] grid-cols-2 gap-6 sm:w-full sm:grid-cols-1">
            {useCases.map((useCase) => (
              <div key={useCase.id} className="group relative overflow-hidden rounded-xl">
                <div className="aspect-video w-full overflow-hidden">
                  <VideoLazyLoad
                    videoSrc={useCase.videoSrc}
                    poster={useCase.videoPoster}
                    mobilePoster={useCase.mobilePoster}
                    className="h-full w-full cursor-pointer object-cover transition-all duration-500"
                    hoverPlay={true}
                    alt={`${useCase.title} using Vidu reference to video`}
                  />
                </div>
                <div className="mt-4 text-center">
                  <h3 className="mb-2 font-medium text-lg text-white">{useCase.title}</h3>
                  <p className="font-normal text-gray-300 text-sm">{useCase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ProductSteps title={t('step.title')} items={stepItems} page="ai-reference-to-video" theme="dark" />

      {/* 对比表格 */}
      <ComparisonSection />

      {/* 保持视频一致性：常见问题与解决方法 */}
      <FeatureCarousel
        title={t('keepConsistent.title')}
        features={keepConsistentIssues}
        buttonText={t('buttons.generateNow')}
        onButtonClick={handleGenerateClick}
        titleClassName="w-[74vw]"
        contentClassName="w-[74vw]"
      />

      {/* Related Products Section */}
      <RelatedProductsSection currentSlug="ai-reference-to-video" />

      <QuestionSection />

      <section className="relative mb-[120px] h-[433px] w-full sm:h-[219px]">
        {/* 视频背景 */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <Image
            src="https://image01.cf.vidu.studio/vidu/media-asset/cta-09d87dba.webp"
            alt="Try Vidu reference to video — create consistent AI videos"
            fill
            className="object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>

        {/* 内容覆盖层 */}
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-8">
            <h2 className="mb-8 font-bold text-[42px] sm:font-semibold sm:text-[32px]">{t('cta')}</h2>
            <Button
              className="h-[60px] min-w-[224px] rounded-[100px] bg-[#0086FF] px-10 py-5 font-medium text-white text-xl shadow-lg transition-all hover:shadow-xl sm:h-12 sm:w-[195px] sm:py-4 sm:text-base"
              onClick={handleGenerateClick}
            >
              {t('buttons.generateNow')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
